import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  Store, TrendingUp, Users, ShieldCheck, Zap, Globe, 
  ArrowLeft, Building2, Hash, Wrench, Car, BadgeCheck, 
  Sparkles, Phone, ChevronLeft, Play, CheckCircle2,
  Star, ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

// Logo URLs - CloudGM Business (Light/Dark variants)
const LOGO_LIGHT = "/logo-light.png"; // Black text for light theme
const LOGO_DARK = "/logo-dark.png";   // White text for dark theme

export default function Home() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('business_token');
      const user = localStorage.getItem('business_user');
      setIsLoggedIn(!!token && !!user);
    };
    
    // Check on mount
    checkLoginStatus();
    
    // Listen for logout events
    const handleLogout = () => {
      setIsLoggedIn(false);
    };
    
    const handleLogin = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('userLoggedOut', handleLogout);
    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('userLoggedOut', handleLogout);
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      Icon: Store,
      title: 'متاجر متعددة الأنواع',
      titleEn: 'Multi-Type Stores',
      description: 'سيارات، عقارات، لوحات، أرقام هواتف، قطع غيار',
      descriptionEn: 'Cars, Real Estate, Plates, Phone Numbers, Spare Parts'
    },
    {
      Icon: Sparkles,
      title: 'ذكاء اصطناعي متقدم',
      titleEn: 'Advanced AI',
      description: 'تحليل صور السيارات وتعبئة البيانات تلقائياً',
      descriptionEn: 'Auto-analyze car images and fill data automatically'
    },
    {
      Icon: TrendingUp,
      title: 'تحليلات فورية',
      titleEn: 'Real-time Analytics',
      description: 'تتبع مبيعاتك وزوارك لحظة بلحظة',
      descriptionEn: 'Track your sales and visitors in real-time'
    },
    {
      Icon: ShieldCheck,
      title: 'أمان معتمد',
      titleEn: 'Certified Security',
      description: 'تشفير متقدم وحماية كاملة لبياناتك',
      descriptionEn: 'Advanced encryption and full data protection'
    },
    {
      Icon: Globe,
      title: 'انتشار واسع',
      titleEn: 'Wide Reach',
      description: 'آلاف العملاء يبحثون عن منتجاتك يومياً',
      descriptionEn: 'Thousands of customers search for your products daily'
    },
    {
      Icon: Zap,
      title: 'سرعة فائقة',
      titleEn: 'Lightning Fast',
      description: 'أنشئ متجرك في دقائق وابدأ البيع فوراً',
      descriptionEn: 'Create your store in minutes and start selling instantly'
    }
  ];

  const storeTypes = [
    { name: 'سيارات', nameEn: 'Automobiles', Icon: Car, gradient: 'from-emerald-500 to-teal-600' },
    { name: 'عقارات', nameEn: 'Real Estate', Icon: Building2, gradient: 'from-blue-500 to-indigo-600' },
    { name: 'لوحات سيارات', nameEn: 'Car Plates', Icon: BadgeCheck, gradient: 'from-amber-500 to-orange-600' },
    { name: 'أرقام هواتف', nameEn: 'Phone Numbers', Icon: Phone, gradient: 'from-[#1E9BD9] to-[#0D7AB5]' },
    { name: 'قطع غيار', nameEn: 'Spare Parts', Icon: Wrench, gradient: 'from-red-500 to-red-600' },
    { name: 'تجارة إلكترونية', nameEn: 'E-Commerce', Icon: Store, gradient: 'from-cyan-500 to-blue-600' }
  ];

  const stats = [
    { value: '10,000+', label: 'تاجر نشط', labelEn: 'Active Merchants' },
    { value: '500K+', label: 'منتج معروض', labelEn: 'Listed Products' },
    { value: '2M+', label: 'زائر شهري', labelEn: 'Monthly Visitors' },
    { value: '99.9%', label: 'وقت التشغيل', labelEn: 'Uptime' }
  ];

  const testimonials = [
    {
      name: 'محمد الكعبي',
      nameEn: 'Mohammed Al Kaabi',
      role: 'تاجر سيارات - دبي',
      roleEn: 'Auto Dealer - Dubai',
      text: 'منصة رائعة! زادت مبيعاتي 300% خلال 3 أشهر',
      textEn: 'Amazing platform! My sales increased 300% in 3 months',
      rating: 5
    },
    {
      name: 'سارة العلي',
      nameEn: 'Sara Al Ali',
      role: 'وكيلة عقارية - أبوظبي',
      roleEn: 'Real Estate Agent - Abu Dhabi',
      text: 'سهولة الاستخدام والتصميم الاحترافي جعلا عملي أسهل',
      textEn: 'Ease of use and professional design made my work easier',
      rating: 5
    },
    {
      name: 'أحمد الشامسي',
      nameEn: 'Ahmed Al Shamsi',
      role: 'تاجر أرقام مميزة',
      roleEn: 'VIP Numbers Dealer',
      text: 'أفضل منصة للتجارة في الإمارات بدون منازع',
      textEn: 'The best trading platform in UAE, no doubt',
      rating: 5
    }
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden cgm-page`}>
      
      {/* Hero Section - Compact */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Gradient Orbs */}
          <div className="absolute top-20 start-10 w-[500px] h-[500px] rounded-full bg-[#38BDF8]/20 blur-[120px] animate-pulse" />
          <div className="absolute bottom-20 end-10 w-[400px] h-[400px] rounded-full bg-[#F97316]/20 blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          
          {/* Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(#38BDF8 1px, transparent 1px), linear-gradient(90deg, #38BDF8 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-8 animate-fade-in-up">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {language === 'ar' ? 'المنصة #1 للتجارة في الإمارات' : '#1 Trading Platform in UAE'}
                </span>
              </div>

              {/* Main Heading */}
              <h1 className="text-hero" data-testid="hero-title">
                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                  {language === 'ar' ? 'أطلق تجارتك مع' : 'Launch Your Business With'}
                </span>
                <br />
                <span className="text-gradient">
                  CLOUDGM BUSINESS
                </span>
              </h1>

              {/* Description */}
              <p className={`text-lg md:text-xl leading-relaxed max-w-xl ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'ar' 
                  ? 'منصة متكاملة لإنشاء وإدارة متاجرك الإلكترونية. سيارات، عقارات، لوحات مميزة، وأكثر. كل ما تحتاجه في مكان واحد.'
                  : 'Complete platform to create and manage your online stores. Cars, real estate, premium plates, and more. Everything you need in one place.'
                }
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                {isLoggedIn ? (
                  <>
                    <Link to="/stores/create">
                      <button className="btn-gradient group" data-testid="create-store-btn">
                        <span className="flex items-center gap-2">
                          {language === 'ar' ? 'أنشئ متجرك الآن' : 'Create Your Store'}
                          <ArrowLeft className="w-5 h-5 rtl:rotate-180 group-hover:-translate-x-1 transition-transform" />
                        </span>
                      </button>
                    </Link>
                    <Link to="/stores">
                      <button className={`btn-glass ${isDarkMode ? '' : 'text-gray-900 border-gray-300'}`}>
                        <span className="flex items-center gap-2">
                          {language === 'ar' ? 'متاجري' : 'My Stores'}
                        </span>
                      </button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/register">
                      <button className="cgm-cta-primary group" data-testid="get-started-btn">
                        <span className="flex items-center gap-2">
                          {language === 'ar' ? 'ابدأ الآن مجاناً' : 'Start Free Now'}
                          <ArrowLeft className="w-5 h-5 rtl:rotate-180 group-hover:-translate-x-1 transition-transform" />
                        </span>
                      </button>
                    </Link>
                    <Link to="/login">
                      <button className="cgm-btn-secondary" data-testid="watch-video-btn">
                        <Play className="w-5 h-5" />
                        {language === 'ar' ? 'شاهد المميزات' : 'View Features'}
                      </button>
                    </Link>
                  </>
                )}
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3 rtl:space-x-reverse">
                  {[1,2,3,4,5].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center border-2 border-white dark:border-[#0B0F19]">
                      <span className="text-white text-xs font-bold">{i}K</span>
                    </div>
                  ))}
                </div>
                <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <span className="text-sm">{language === 'ar' ? '+10,000 تاجر راضٍ' : '10,000+ Happy Merchants'}</span>
                </div>
              </div>
            </div>

            {/* Visual Element - Dashboard Preview */}
            <div className="relative hidden lg:block">
              <div className="relative animate-float">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#38BDF8] to-[#F97316] rounded-3xl blur-2xl opacity-30" />
                
                {/* Main Card */}
                <div className="relative card-tactile p-6 rounded-3xl">
                  {/* Logo */}
                  <div className="flex items-center justify-between mb-6">
                    <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="CloudGM" className="h-10" />
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 text-green-500 text-sm">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      {language === 'ar' ? 'متصل' : 'Online'}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      { label: language === 'ar' ? 'المبيعات' : 'Sales', value: '125,430', color: 'text-[#38BDF8]', trend: '+24%' },
                      { label: language === 'ar' ? 'الطلبات' : 'Orders', value: '1,847', color: 'text-[#F97316]', trend: '+18%' },
                      { label: language === 'ar' ? 'الزوار' : 'Visitors', value: '45.2K', color: 'text-emerald-500', trend: '+32%' },
                      { label: language === 'ar' ? 'التقييم' : 'Rating', value: '4.9', color: 'text-amber-500', trend: '★★★★★' }
                    ].map((stat, i) => (
                      <div key={i} className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</p>
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                        <p className="text-xs text-green-500">{stat.trend}</p>
                      </div>
                    ))}
                  </div>

                  {/* Chart Placeholder */}
                  <div className={`h-32 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'} flex items-end justify-around p-4`}>
                    {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                      <div 
                        key={i} 
                        className="w-6 rounded-t-lg gradient-primary opacity-80"
                        style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -end-4 p-3 rounded-xl glass-card animate-float" style={{ animationDelay: '0.5s' }}>
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div className="absolute -bottom-4 -start-4 p-3 rounded-xl glass-card animate-float" style={{ animationDelay: '1s' }}>
                  <Sparkles className="w-6 h-6 text-[#F97316]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className={`w-6 h-10 rounded-full border-2 ${isDarkMode ? 'border-white/30' : 'border-gray-400'} flex justify-center pt-2`}>
            <div className="w-1 h-2 rounded-full bg-gradient-to-b from-[#38BDF8] to-[#F97316]" />
          </div>
        </div>
      </section>

      {/* Store Types Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-section mb-4">
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                {language === 'ar' ? 'أنواع المتاجر' : 'Store Types'}
              </span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'ar' 
                ? 'اختر نوع متجرك وابدأ رحلتك التجارية اليوم'
                : 'Choose your store type and start your business journey today'
              }
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 cgm-reveal-stagger">
            {storeTypes.map((type, index) => (
              <div 
                key={index}
                className="cgm-ai-card-interactive text-center group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${type.gradient} flex items-center justify-center group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-lg`}>
                  <type.Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className={`font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? type.name : type.nameEn}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#38BDF8]/5 to-transparent pointer-events-none" />
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-gradient text-sm font-bold tracking-wider uppercase mb-4 block">
              {language === 'ar' ? 'لماذا CloudGM؟' : 'Why CloudGM?'}
            </span>
            <h2 className="text-section mb-4">
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                {language === 'ar' ? 'مميزات لا تُضاهى' : 'Unmatched Features'}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 cgm-reveal-stagger">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`cgm-ai-card-interactive group ${activeFeature === index ? '!border-[#38BDF8]/50' : ''}`}
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="cgm-icon-glow !w-14 !h-14 !p-3 mb-4 group-hover:scale-110 transition-transform duration-500">
                  <feature.Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-xl font-black mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? feature.title : feature.titleEn}
                </h3>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                  {language === 'ar' ? feature.description : feature.descriptionEn}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 gradient-primary opacity-10 pointer-events-none" />
        
        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-black text-gradient mb-2">
                  {stat.value}
                </div>
                <div className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {language === 'ar' ? stat.label : stat.labelEn}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-section mb-4">
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                {language === 'ar' ? 'ماذا يقول تجارنا' : 'What Our Merchants Say'}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 cgm-reveal-stagger">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="cgm-ai-card group">
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className={`text-lg mb-6 leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  "{language === 'ar' ? testimonial.text : testimonial.textEn}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="cgm-icon-glow !w-12 !h-12 !p-0 flex items-center justify-center text-white font-black group-hover:scale-110 transition-transform duration-500">
                    {(language === 'ar' ? testimonial.name : testimonial.nameEn).charAt(0)}
                  </div>
                  <div>
                    <p className={`font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                      {language === 'ar' ? testimonial.name : testimonial.nameEn}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {language === 'ar' ? testimonial.role : testimonial.roleEn}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 gradient-primary pointer-events-none" />
            <div className="absolute inset-0 bg-black/20 pointer-events-none" />
            
            {/* Content */}
            <div className="relative p-12 md:p-20 text-center text-white">
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                {language === 'ar' ? 'جاهز لبدء رحلتك؟' : 'Ready to Start?'}
              </h2>
              <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                {language === 'ar' 
                  ? 'انضم لآلاف التجار الناجحين وابدأ ببيع منتجاتك اليوم'
                  : 'Join thousands of successful merchants and start selling your products today'
                }
              </p>
              
              {!isLoggedIn && (
                <Link to="/auth">
                  <button className="bg-white text-gray-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2 group">
                    {language === 'ar' ? 'سجّل الآن مجاناً' : 'Sign Up Free Now'}
                    <ArrowLeft className="w-5 h-5 rtl:rotate-180 group-hover:-translate-x-1 transition-transform" />
                  </button>
                </Link>
              )}
              
              <p className="mt-6 text-sm opacity-75">
                {language === 'ar' ? 'لا حاجة لبطاقة ائتمان • إعداد في دقائق' : 'No credit card required • Setup in minutes'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
